import React, { useState } from 'react';
import Section from '../ui/Section.jsx';
import MeetingSummaryModal from '../modals/MeetingSummaryModal.jsx';
import MeetingSummaryViewModal from '../modals/MeetingSummaryViewModal.jsx';
import Pagination, { usePagination } from '../ui/Pagination.jsx';

function firstLine(body) {
  return (body || '').split('\n').map(l => l.trim()).find(Boolean) || body;
}

async function deleteSummary(id) {
  const res = await fetch(`/meeting_summaries/${id}`, {
    method:      'DELETE',
    credentials: 'same-origin',
    headers: {
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      'Accept':       'application/json'
    }
  });
  if (res.status === 401) { window.location = '/session/new'; return false; }
  if (res.status === 403) {
    window.alert("You don't have permission — only the author or a project manager can delete this summary.");
    return false;
  }
  if (!res.ok && res.status !== 204) throw new Error(`DELETE failed (${res.status})`);
  return true;
}

function SummaryRow({ summary, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(summary)}
      className="w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left hover:bg-[#FBFBF8]"
      style={{ background: '#fff', borderColor: '#ECEBE5', cursor: 'pointer' }}
    >
      <span className="text-[12px] font-medium shrink-0" style={{ color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>
        {summary.held_on}
      </span>
      <span
        className="text-[13px] truncate"
        style={{ color: '#3A3A38', flex: 1, minWidth: 0 }}
      >
        {firstLine(summary.body)}
      </span>
      <span className="text-[14px] shrink-0" style={{ color: '#B7B7B0' }} aria-hidden="true">›</span>
    </button>
  );
}

export default function DailyMeetings({ project }) {
  const [summaries, setSummaries] = useState(project.meeting_summaries || []);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const { pageItems, ...pager } = usePagination(summaries, 10);

  function openView(summary) {
    setViewing(summary);
    setViewOpen(true);
  }

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(summary) {
    setEditing(summary);
    setModalOpen(true);
  }

  function editFromView(summary) {
    setViewOpen(false);
    openEdit(summary);
  }

  async function deleteFromView(summary) {
    if (!window.confirm('Delete this summary?')) return;
    const ok = await deleteSummary(summary.id);
    if (ok) {
      handleDeleted(summary.id);
      setViewOpen(false);
    }
  }

  function handleSaved(saved) {
    if (editing) {
      setSummaries(prev => prev.map(s => s.id === saved.id ? saved : s));
    } else {
      // Insert in date order (newest first)
      setSummaries(prev => [saved, ...prev].sort((a, b) => b.held_on.localeCompare(a.held_on)));
    }
  }

  function handleDeleted(id) {
    setSummaries(prev => prev.filter(s => s.id !== id));
  }

  const headerRight = (
    <button
      onClick={openCreate}
      className="text-[12px] px-2.5 py-1 rounded-md border hover:bg-[#FBFBF8]"
      style={{ borderColor: '#E0DFD9', background: '#fff', color: '#1A1A1A', cursor: 'pointer' }}
    >+ Log summary</button>
  );

  return (
    <>
      <Section id="meetings" title="Meetings" count={`${summaries.length} entries`} headerRight={headerRight}>
        {summaries.length === 0 ? (
          <div className="text-[12px]" style={{ color: '#9A9A95' }}>
            No summaries yet. Run your meeting through a notetaker (Fathom, Otter, Gemini Notes…) and paste the result here.
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {pageItems.map(s => (
                <SummaryRow
                  key={s.id}
                  summary={s}
                  onOpen={openView}
                />
              ))}
            </div>
            <Pagination {...pager} />
          </>
        )}
      </Section>
      <MeetingSummaryViewModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        summary={viewing}
        onEdit={editFromView}
        onDelete={deleteFromView}
      />
      <MeetingSummaryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        projectId={project.id}
        summary={editing}
        onSaved={handleSaved}
      />
    </>
  );
}
